import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { instrumentsQueryOptions } from '@/hooks/queries/markets/use-markets';
import { flattenPages, nextCursorParam } from '@/hooks/queries/pagination';
import type { Market } from '@/services/api/markets';
import { buildTradesRequestBody, getTradesPage, type TradeRow, type TradesFilters, type TradesWireResponse } from '@/services/api/trades';

// Module-level selects — stable references preserve TanStack Query's select memoization (see pagination.ts).
const selectAllTrades = flattenPages<TradesWireResponse, TradeRow>((page) => page.trades);
const selectMarketIds = (markets: Market[]): string[] => markets.map((market) => market.id);

export const TRADES_QUERY_KEY = ['trades'] as const;
export const TRADES_LIST_QUERY_KEY = [...TRADES_QUERY_KEY, 'list'] as const;

/**
 * Cursor-paginated trade history (legacy useTrades → useInfiniteQuery).
 * The key carries the endpoint discriminator (accountHolderId) plus the stable
 * wire body, so any filter change rotates the key — the legacy
 * reset-on-filter-change (strictDeepEqual) for free. Pages load on scroll only:
 * deliberately NO auto-fetch-all (unlike terms).
 */
export const tradesInfiniteQueryOptions = (filters: TradesFilters) =>
  infiniteQueryOptions({
    queryKey: [...TRADES_LIST_QUERY_KEY, filters.accountHolderId ?? null, buildTradesRequestBody(filters)],
    queryFn: ({ pageParam }) => getTradesPage({ filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    // Stop when the cursor chain ends — a QuestDB page is never empty while a
    // cursor exists, so the legacy empty-page clause is not needed
    getNextPageParam: nextCursorParam,
    select: selectAllTrades,
  });

export const useTradesInfinite = (filters: TradesFilters) => useInfiniteQuery(tradesInfiniteQueryOptions(filters));

/**
 * Instrument options share the Markets page cache under ['markets','instruments']
 * with ONE queryFn — visiting Trading and Markets no longer fetches
 * the same public endpoint twice. The legacy BFF swallowed fetch errors to [];
 * that behaviour now lives at the consumers, which render options
 * from `data ?? []` and ignore isError — the Market filter chip simply stays
 * hidden on failure instead of surfacing an error.
 */
export const marketsQueryOptions = () =>
  queryOptions({
    ...instrumentsQueryOptions(),
    select: selectMarketIds,
    // Legacy fetched once per table mount; market listings change rarely
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * Market ids for the multi-choice instrument filter: no type/state
 * filtering — ALL market ids (including perps) are options, in endpoint order.
 */
export const useMarketIds = () => useQuery(marketsQueryOptions());
